import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthContext from '../../contexts/auth-context.js';
import { getConcessionAPI } from './dummyLiveData.js';
import { toVenueTime } from './live/liveMetrics';
import moment from 'moment';
import { getMostRecentSeriesAndGame } from '../Retail/helper/helper_functions.js';

const ConcessionLocationController = ({ children }) => {
  const location = useLocation();
  const { state } = location;
  const id = state?.id;
  const currentLocation = state?.currentLocation;
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);

  // State management
  const [loading, setLoading] = useState(true);
  const [dayType, setDayType] = useState("game");
  const [historicalData, setHistoricalData] = useState([]);
  const [historicalTransactionData, setHistoricalTransactionData] = useState([]);
  const [stats, setStats] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentSort, setCurrentSort] = useState({
    value: "id-asc",
    label: "Section ID (Low)",
  });

  // Transaction sorting state
  const [isTransactionDropdownOpen, setIsTransactionDropdownOpen] = useState(false);
  const [currentTransactionSort, setCurrentTransactionSort] = useState({
    value: "sectionId-asc",
    label: "Section ID (Low to High)"
  });

  // Section Analysis sorting state
  const [isSectionAnalysisDropdownOpen, setIsSectionAnalysisDropdownOpen] = useState(false);
  const [currentSectionAnalysisSort, setCurrentSectionAnalysisSort] = useState({
    value: "sectionId-asc",
    label: "Section ID (Low to High)"
  });

  const [isToday, setIsToday] = useState(false);
  const [now, setNow] = useState(
    new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation.timeZone }))
  );
  const [games, setGames] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(null);
  const [currentSeries, setCurrentSeries] = useState(null);
  const [currentGame, setCurrentGame] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);

  // Concession specific data
  const [sectionQueueTimes, setSectionQueueTimes] = useState({});
  const [sectionTransactionTimes, setSectionTransactionTimes] = useState({});
  const [averageTransactionTime, setAverageTransactionTime] = useState(0);
  const [averageQueueTime, setAverageQueueTime] = useState(0);

  const [historicalPeopleCountData, setHistoricalPeopleCountData] = useState([]);
  const [sectionPeopleCounts, setSectionPeopleCounts] = useState({});
  const [averagePeopleCount, setAveragePeopleCount] = useState(0);
  const [countStats, setCountStats] = useState(null);

  const [activeSections, setActiveSections] = useState(0);
  const [totalSections, setTotalSections] = useState(0);

  // Auto refresh
  const [autoFetchEnabled, setAutoFetchEnabled] = useState(false);
  const intervalRef = useRef(null);

  const sections = currentLocation?.sections?.map(section => section.sectionId) || [];

  const sortOptions = [
    {
      value: "id-asc",
      label: "Section ID (Low)",
    },
    {
      value: "id-desc",
      label: "Section ID (High)",
    },
    {
      value: "current-queue-asc",
      label: "Current Queue Time (Low)",
    },
    {
      value: "current-queue-desc",
      label: "Current Queue Time (High)",
    },
  ];

  // Transaction sort options
  const transactionSortOptions = [
    { value: 'sectionId-asc', label: 'Section ID (Low to High)' },
    { value: 'sectionId-desc', label: 'Section ID (High to Low)' },
    { value: 'currentTransaction-asc', label: 'Current Transaction (Low to High)' },
    { value: 'currentTransaction-desc', label: 'Current Transaction (High to Low)' },
    { value: 'avgTransaction-asc', label: 'Average Transaction (Low to High)' },
    { value: 'avgTransaction-desc', label: 'Average Transaction (High to Low)' },
    { value: 'peakTransaction-asc', label: 'Peak Transaction (Low to High)' },
    { value: 'peakTransaction-desc', label: 'Peak Transaction (High to Low)' }
  ];

  const getSectionAnalysisSortOptions = useCallback(() => {
    const isCountMode = currentLocation?.sensorDataType === "count";
    
    if (isCountMode) {
      return [
        { value: 'sectionId-asc', label: 'Section ID (Low to High)' },
        { value: 'sectionId-desc', label: 'Section ID (High to Low)' },
        { value: 'maxCount-asc', label: 'Highest Count (Low to High)' },
        { value: 'maxCount-desc', label: 'Highest Count (High to Low)' },
        { value: 'avgCount-asc', label: 'Average Count (Low to High)' },
        { value: 'avgCount-desc', label: 'Average Count (High to Low)' }
      ];
    } else {
      return [
        { value: 'sectionId-asc', label: 'Section ID (Low to High)' },
        { value: 'sectionId-desc', label: 'Section ID (High to Low)' },
        { value: 'maxQueue-asc', label: 'Longest Queue (Low to High)' },
        { value: 'maxQueue-desc', label: 'Longest Queue (High to Low)' },
        { value: 'avgQueue-asc', label: 'Average Queue (Low to High)' },
        { value: 'avgQueue-desc', label: 'Average Queue (High to Low)' },
        { value: 'maxTransaction-asc', label: 'Peak Transaction (Low to High)' },
        { value: 'maxTransaction-desc', label: 'Peak Transaction (High to Low)' },
        { value: 'avgTransaction-asc', label: 'Average Transaction (Low to High)' },
        { value: 'avgTransaction-desc', label: 'Average Transaction (High to Low)' }
      ];
    }
  }, [currentLocation?.sensorDataType]);
  
  const sectionAnalysisSortOptions = getSectionAnalysisSortOptions();

  // Utility function to format transaction time from seconds
  const formatTransactionTime = useCallback((seconds) => {
    if (typeof seconds !== 'number' || seconds < 0) return '0s';
  
    const totalSeconds = Math.round(seconds);
  
    if (totalSeconds < 60) {
      return `${totalSeconds}s`;
    }
  
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
  
    if (remainingSeconds === 0) {
      return `${minutes} mins`;
    }
  
    return `${minutes}m ${remainingSeconds}s`;
  }, []);

  // Refresh function for today's data
  const refreshRegularDay = useCallback(() => {
    if (currentLocation && selectedDate) {
      fetchConcessionData(true);
    }
  }, [currentLocation, selectedDate]);

  useEffect(() => {
    const interval = setInterval(() => {
      // console.log('Updating the current time...')
      setNow(
        new Date(new Date().toLocaleString('en-US', { timeZone: currentLocation.timeZone }))
      );
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, [currentLocation.timeZone]);

  useEffect(() => {
    getGames();
  }, [currentLocation]);

  useEffect(() => {
    const nowMoment = moment(now);
    const adjustedNow = nowMoment.hour() < 3
      ? nowMoment.subtract(1, "day").startOf("day")
      : nowMoment.startOf("day");
  
    const gameDateMatch =
      currentGame?.date &&
      moment(currentGame.date).startOf("day").isSame(adjustedNow);
  
    setIsToday(gameDateMatch);
    if (gameDateMatch) {
        setAutoFetchEnabled(true);
    } else {
        setAutoFetchEnabled(false);
    }
  }, [currentGame, selectedDate, now]);

  const getGames = () => {
      setGames([])
    
      getConcessionAPI(currentLocation)(
        "get-location-current-schedule",
        { 
          venue: authCtx.venue,
          locationId: currentLocation.id,
          measurement: currentLocation.measurement,
        },
        (data_, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
            } else {
              alert("Unable to fetch data. Please refresh the page and try again.");
            }
          } else {
            const sortedGames = [...data_].sort((a, b) =>
              moment(b.dates[0].date).valueOf() - moment(a.dates[0].date).valueOf()
            );
            const mostRecentYear = moment(sortedGames[0]?.dates[0]?.date).format("YYYY");
  
            if (!currentSeason) {
              setCurrentSeason(mostRecentYear);
              const adjustedNow = moment(now).tz(currentLocation.timeZone).subtract(3, 'hours').toDate();
  
              const { recentSeries, recentGame } = getMostRecentSeriesAndGame(data_, adjustedNow);
              if (recentGame) {
                const recentGameYear = moment(recentGame.date).format("YYYY");
                setCurrentSeason(recentGameYear);
              }
              setCurrentSeries(recentSeries);
              setCurrentGame(recentGame);
            }
            setGames(data_);
          }
        },
        authCtx.token
      );
    };

  // Fetch concession data
  const fetchConcessionData = useCallback((isBackgroundUpdate = false) => {

    return new Promise((resolve, reject) => {
      if (!currentLocation || !selectedDate) {
        // console.log('Missing required data for fetch');
        reject(new Error('Missing required data'));
        return;
      }

      // console.log('Starting concession data fetch...');
      
      if (!isBackgroundUpdate) {
        setLoading(true);
      }

      const dateStr = moment(selectedDate).format('YYYY-MM-DD');

      const getConcessionsDailyDataRoute = "get-concession-location-daily-data-v2";

      getConcessionAPI(currentLocation)(
        getConcessionsDailyDataRoute,
        {
          sections: currentLocation.sections.map(section => section.sectionId),
          venue: authCtx.venue,
          locationId: currentLocation.id,
          measurement: currentLocation.measurement || "",
          timezone: currentLocation.timeZone,
          date: dateStr
        },
        (data, error) => {
          if (error) {
            if (error.status === 401) {
              authCtx.logout();
              navigate("/auth");
              reject(new Error('unauthorized'));
            } else {
              console.error('Failed to fetch concession data:', error);
              reject(new Error('failed'));
            }
          } else if (data) {
            const isCountMode = currentLocation.sensorDataType === "count";
            
            const processedHistoricalData = [];
            const processedHistoricalTransactionData = [];
            const processedHistoricalCountData = [];
            const latestQueueTimes = {};
            const latestSectionTransactionTimes = {};
            const latestSectionCounts = {};

            let activeCount = 0;
            let totalCount = 0;

            data.forEach(sectionData => {
              const sectionId = Object.keys(sectionData)[0];
              const dataPoints = sectionData[sectionId];

              if (dataPoints && dataPoints.length > 0) {
                const latestPoint = dataPoints[dataPoints.length - 1];
                
                if (isCountMode) {
                  latestSectionCounts[sectionId] = latestPoint.peopleCount || 0;
                  
                  if ((latestPoint.peopleCount || 0) > 0) {
                    activeCount++;
                  }
                  totalCount++;

                  const countDataPoints = dataPoints.map(point => ({
                    x: toVenueTime(point.time, currentLocation.timeZone),
                    y: point.peopleCount || 0
                  }));

                  processedHistoricalCountData.push({
                    section: sectionId,
                    data: countDataPoints
                  });

                } else {
                  latestQueueTimes[sectionId] = latestPoint.queueTime;
                  latestSectionTransactionTimes[sectionId] = latestPoint.transactionTime;

                  if (latestPoint.queueTime > 0) {
                    activeCount++;
                  }
                  totalCount++;

                  const queueDataPoints = dataPoints.map(point => ({
                    x: toVenueTime(point.time, currentLocation.timeZone),
                    y: point.queueTime,
                    overflow: point.overflow || 0
                  }));

                  const transactionDataPoints = dataPoints.map(point => ({
                    x: toVenueTime(point.time, currentLocation.timeZone),
                    y: point.transactionTime || 0
                  }));

                  processedHistoricalData.push({
                    section: sectionId,
                    data: queueDataPoints
                  });

                  processedHistoricalTransactionData.push({
                    section: sectionId,
                    data: transactionDataPoints
                  });
                }
              }
            });

            if (isCountMode) {
              const activeCounts = Object.values(latestSectionCounts).filter(count => count > 0);
              const avgPeopleCount = activeCounts.length > 0 
                ? Math.round(activeCounts.reduce((a, b) => a + b, 0) / activeCounts.length) 
                : 0;

              setHistoricalPeopleCountData(processedHistoricalCountData);
              setSectionPeopleCounts(latestSectionCounts);
              setAveragePeopleCount(avgPeopleCount);
              setActiveSections(activeCount);
              setTotalSections(totalCount);

              // console.log('Concession count data fetch completed successfully');
              resolve({
                locationId: currentLocation.id,
                type: 'concession',
                data: {
                  type: 'people count',
                  averagePeopleCount: avgPeopleCount,
                  sectionCounts: latestSectionCounts,
                  activeSections: activeCount,
                  totalSections: totalCount,
                  historicalCountData: processedHistoricalCountData
                }
              });

            } else {
              const activeTimes = Object.values(latestQueueTimes).filter(time => time > 0);
              const avgQueueTime = activeTimes.length > 0 
                ? Math.round(activeTimes.reduce((a, b) => a + b, 0) / activeTimes.length) 
                : 0;

              const activeTransactionTimes = Object.values(latestSectionTransactionTimes).filter(time => time > 0);
              const avgTransactionTime = activeTransactionTimes.length > 0 
                  ? Math.round(activeTransactionTimes.reduce((a, b) => a + b, 0) / activeTransactionTimes.length) 
                  : 0;

              setHistoricalData(processedHistoricalData);
              setHistoricalTransactionData(processedHistoricalTransactionData);
              setSectionQueueTimes(latestQueueTimes);
              setSectionTransactionTimes(latestSectionTransactionTimes);
              setAverageTransactionTime(avgTransactionTime);
              setAverageQueueTime(avgQueueTime);
              setActiveSections(activeCount);
              setTotalSections(totalCount);

              // console.log('Concession queue data fetch completed successfully');
              resolve({
                locationId: currentLocation.id,
                type: 'concession',
                data: {
                  type: 'queue and transaction time',
                  averageQueueTime: avgQueueTime,
                  averageTransactionTime: avgTransactionTime,
                  sectionQueueTimes: latestQueueTimes,
                  sectionTransactionTimes: latestSectionTransactionTimes,
                  activeSections: activeCount,
                  totalSections: totalCount,
                  historicalData: processedHistoricalData,
                  historicalTransactionData: processedHistoricalTransactionData
                }
              });
            }
          } else {
            console.error('No data received from API');
            reject(new Error('invalid'));
          }

          if (!isBackgroundUpdate) {
            setLoading(false);
          }
        },
        authCtx.token
      );
    });
  }, [authCtx.token, authCtx.venue, currentLocation, navigate, selectedDate]);

 // Complete calculateStats function with transaction processing and fixed delta calculation
const calculateStats = useCallback((historicalData, sectionQueueTimes, historicalTransactionData, sectionTransactionTimes) => {
    if (!historicalData?.length) return null;
  
    const currentQueueTimes = Object.values(sectionQueueTimes).filter(time => typeof time === 'number' && time >= 0);
    const currentTransactionTimes = Object.values(sectionTransactionTimes).filter(time => typeof time === 'number' && time >= 0);
    
    const currentStats = {
      maxQueue: currentQueueTimes.length > 0 ? Math.max(...currentQueueTimes) : 0,
      minQueue: currentQueueTimes.length > 0 ? Math.min(...currentQueueTimes) : 0,
      avgQueue: currentQueueTimes.length > 0 ? Math.round(currentQueueTimes.reduce((sum, time) => sum + time, 0) / currentQueueTimes.length) : 0,
      maxTransaction: currentTransactionTimes.length > 0 ? Math.max(...currentTransactionTimes) : 0,
      minTransaction: currentTransactionTimes.length > 0 ? Math.min(...currentTransactionTimes) : 0,
      avgTransaction: currentTransactionTimes.length > 0 ? Math.round(currentTransactionTimes.reduce((sum, time) => sum + time, 0) / currentTransactionTimes.length) : 0
    };
  
    currentStats.queueDelta = currentStats.maxQueue - currentStats.minQueue;
    currentStats.transactionDelta = currentStats.maxTransaction - currentStats.minTransaction;
  
    const allDataByMinute = {};
    const allTransactionDataByMinute = {};
    
    historicalData.forEach((sectionData) => {
      sectionData.data.forEach(point => {
        if (typeof point.y !== 'number' || point.y < 0) return;
        
        const minuteKey = new Date(point.x).toISOString().slice(0, 16);
        
        if (!allDataByMinute[minuteKey]) {
          allDataByMinute[minuteKey] = {};
        }
        
        if (!allDataByMinute[minuteKey][sectionData.section]) {
          allDataByMinute[minuteKey][sectionData.section] = [];
        }
        
        allDataByMinute[minuteKey][sectionData.section].push(point.y);
      });
    });
  
    if (historicalTransactionData?.length) {
      historicalTransactionData.forEach((sectionData) => {
        sectionData.data.forEach(point => {
          if (typeof point.y !== 'number' || point.y < 0) return;
          
          const minuteKey = new Date(point.x).toISOString().slice(0, 16);
          
          if (!allTransactionDataByMinute[minuteKey]) {
            allTransactionDataByMinute[minuteKey] = {};
          }
          
          if (!allTransactionDataByMinute[minuteKey][sectionData.section]) {
            allTransactionDataByMinute[minuteKey][sectionData.section] = [];
          }
          
          allTransactionDataByMinute[minuteKey][sectionData.section].push(point.y);
        });
      });
    }
  
    const minuteStats = [];
    
    const allMinutes = new Set([
      ...Object.keys(allDataByMinute),
      ...Object.keys(allTransactionDataByMinute)
    ]);
  
    Array.from(allMinutes).forEach(minuteKey => {
      const queueSectionsData = allDataByMinute[minuteKey] || {};
      const transactionSectionsData = allTransactionDataByMinute[minuteKey] || {};
      
      const sectionQueueMaxes = [];
      Object.entries(queueSectionsData).forEach(([sectionId, queueTimes]) => {
        const maxForSection = Math.max(...queueTimes);
        sectionQueueMaxes.push(maxForSection);
      });
      
      const sectionTransactionMaxes = [];
      Object.entries(transactionSectionsData).forEach(([sectionId, transactionTimes]) => {
        const maxForSection = Math.max(...transactionTimes);
        sectionTransactionMaxes.push(maxForSection);
      });
      
      const minuteStat = {
        minute: minuteKey,
        queueMax: sectionQueueMaxes.length > 0 ? Math.max(...sectionQueueMaxes) : 0,
        queueMin: sectionQueueMaxes.length > 0 ? Math.min(...sectionQueueMaxes) : 0,
        queueAvg: sectionQueueMaxes.length > 0 ? sectionQueueMaxes.reduce((a, b) => a + b, 0) / sectionQueueMaxes.length : 0,
        transactionMax: sectionTransactionMaxes.length > 0 ? Math.max(...sectionTransactionMaxes) : 0,
        transactionMin: sectionTransactionMaxes.length > 0 ? Math.min(...sectionTransactionMaxes) : 0,
        transactionAvg: sectionTransactionMaxes.length > 0 ? sectionTransactionMaxes.reduce((a, b) => a + b, 0) / sectionTransactionMaxes.length : 0
      };
  
      minuteStat.queueDelta = minuteStat.queueMax - minuteStat.queueMin;
      minuteStat.transactionDelta = minuteStat.transactionMax - minuteStat.transactionMin;
  
      minuteStats.push(minuteStat);
    });
  
    minuteStats.sort((a, b) => new Date(a.minute) - new Date(b.minute));
  
    let historicalStats = {
      maxDelta: 0,
      maxDeltaTime: null,
      minDelta: Infinity,
      minDeltaTime: null,
      busiestTime: null,
      busiestAvgQueue: 0,
      quietestTime: null,
      quietestAvgQueue: Infinity,
      maxTransactionDelta: 0,
      maxTransactionDeltaTime: null,
      peakTransactionTime: null,
      peakTransactionAvg: 0
    };
    
    minuteStats.forEach(stat => {
      if (stat.queueDelta > historicalStats.maxDelta) {
        historicalStats.maxDelta = stat.queueDelta;
        historicalStats.maxDeltaTime = stat.minute;
      }
      if (stat.queueDelta < historicalStats.minDelta) {
        historicalStats.minDelta = stat.queueDelta;
        historicalStats.minDeltaTime = stat.minute;
      }
  
      if (stat.queueAvg > historicalStats.busiestAvgQueue) {
        historicalStats.busiestAvgQueue = stat.queueAvg;
        historicalStats.busiestTime = stat.minute;
      }
      if (stat.queueAvg < historicalStats.quietestAvgQueue) {
        historicalStats.quietestAvgQueue = stat.queueAvg;
        historicalStats.quietestTime = stat.minute;
      }
  
      if (stat.transactionDelta > historicalStats.maxTransactionDelta) {
        historicalStats.maxTransactionDelta = stat.transactionDelta;
        historicalStats.maxTransactionDeltaTime = stat.minute;
      }
  
      if (stat.transactionAvg > historicalStats.peakTransactionAvg) {
        historicalStats.peakTransactionAvg = stat.transactionAvg;
        historicalStats.peakTransactionTime = stat.minute;
      }
    });
  
    const formatTime = (minuteKey) => {
      return minuteKey ? new Date(minuteKey + ':00.000Z').toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }) : null;
    };
  
    if (historicalStats.minDelta === Infinity) {
      historicalStats.minDelta = 0;
      historicalStats.minDeltaTime = null;
    }
    if (historicalStats.quietestAvgQueue === Infinity) {
      historicalStats.quietestAvgQueue = 0;
      historicalStats.quietestTime = null;
    }
  
    const dayAverages = {
      queue: minuteStats.length > 0 ? Math.round(minuteStats.reduce((sum, stat) => sum + stat.queueAvg, 0) / minuteStats.length) : 0,
      delta: minuteStats.length > 0 ? Math.round(minuteStats.reduce((sum, stat) => sum + stat.queueDelta, 0) / minuteStats.length) : 0,
      transaction: minuteStats.length > 0 ? Math.round(minuteStats.reduce((sum, stat) => sum + stat.transactionAvg, 0) / minuteStats.length) : 0
    };
  
    const sectionStats = {};
    
    historicalData.forEach((sectionData, index) => {
      const sectionId = sectionData.section;
      const transactionData = historicalTransactionData?.[index];
      
      if (!sectionData.data?.length) return;
  
      const sectionQueueByMinute = {};
      sectionData.data.forEach(point => {
        if (typeof point.y !== 'number' || point.y < 0) return;
        const minuteKey = new Date(point.x).toISOString().slice(0, 16);
        if (!sectionQueueByMinute[minuteKey]) {
          sectionQueueByMinute[minuteKey] = [];
        }
        sectionQueueByMinute[minuteKey].push(point.y);
      });
  
      const sectionTransactionByMinute = {};
      if (transactionData?.data?.length) {
        transactionData.data.forEach(point => {
          if (typeof point.y !== 'number' || point.y < 0) return;
          const minuteKey = new Date(point.x).toISOString().slice(0, 16);
          if (!sectionTransactionByMinute[minuteKey]) {
            sectionTransactionByMinute[minuteKey] = [];
          }
          sectionTransactionByMinute[minuteKey].push(point.y);
        });
      }
  
      let sectionTotals = {
        maxQueue: 0,
        minQueue: Infinity,
        totalQueue: 0,
        maxTransaction: 0,
        minTransaction: Infinity,
        totalTransaction: 0,
        maxQueueTime: null,
        minQueueTime: null,
        maxTransactionTime: null,
        minTransactionTime: null,
        queueDataPoints: 0,
        transactionDataPoints: 0
      };
  
      Object.entries(sectionQueueByMinute).forEach(([minuteKey, queueTimes]) => {
        const maxQueueForMinute = Math.max(...queueTimes);
        
        sectionTotals.totalQueue += maxQueueForMinute;
        sectionTotals.queueDataPoints++;
  
        if (maxQueueForMinute > sectionTotals.maxQueue) {
          sectionTotals.maxQueue = maxQueueForMinute;
          sectionTotals.maxQueueTime = new Date(minuteKey + ':00.000Z');
        }
        if (maxQueueForMinute < sectionTotals.minQueue) {
          sectionTotals.minQueue = maxQueueForMinute;
          sectionTotals.minQueueTime = new Date(minuteKey + ':00.000Z');
        }
      });
  
      Object.entries(sectionTransactionByMinute).forEach(([minuteKey, transactionTimes]) => {
        const maxTransactionForMinute = Math.max(...transactionTimes);
        
        sectionTotals.totalTransaction += maxTransactionForMinute;
        sectionTotals.transactionDataPoints++;
  
        if (maxTransactionForMinute > sectionTotals.maxTransaction) {
          sectionTotals.maxTransaction = maxTransactionForMinute;
          sectionTotals.maxTransactionTime = new Date(minuteKey + ':00.000Z');
        }
        if (maxTransactionForMinute < sectionTotals.minTransaction) {
          sectionTotals.minTransaction = maxTransactionForMinute;
          sectionTotals.minTransactionTime = new Date(minuteKey + ':00.000Z');
        }
      });
  
      if (sectionTotals.queueDataPoints > 0 || sectionTotals.transactionDataPoints > 0) {
        sectionStats[sectionId] = {
          maxQueue: sectionTotals.maxQueue,
          minQueue: sectionTotals.minQueue === Infinity ? 0 : sectionTotals.minQueue,
          avgQueue: sectionTotals.queueDataPoints > 0 ? Math.round(sectionTotals.totalQueue / sectionTotals.queueDataPoints) : 0,
          maxQueueTime: sectionTotals.maxQueueTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          minQueueTime: sectionTotals.minQueueTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          maxTransaction: Math.round(sectionTotals.maxTransaction),
          minTransaction: sectionTotals.minTransaction === Infinity ? 0 : Math.round(sectionTotals.minTransaction),
          avgTransaction: sectionTotals.transactionDataPoints > 0 ? Math.round(sectionTotals.totalTransaction / sectionTotals.transactionDataPoints) : 0,
          maxTransactionTime: sectionTotals.maxTransactionTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          minTransactionTime: sectionTotals.minTransactionTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })
        };
      }
    });
  
    return {
      currentMaxQueue: currentStats.maxQueue,
      currentMinQueue: currentStats.minQueue,
      currentAvgQueue: currentStats.avgQueue,
      currentDelta: currentStats.queueDelta,
      currentMaxTransaction: currentStats.maxTransaction,
      currentMinTransaction: currentStats.minTransaction,
      currentAvgTransaction: currentStats.avgTransaction,
      currentTransactionDelta: currentStats.transactionDelta,
      historicalMaxDelta: historicalStats.maxDelta,
      historicalMaxDeltaTime: formatTime(historicalStats.maxDeltaTime),
      historicalMinDelta: historicalStats.minDelta,
      historicalMinDeltaTime: formatTime(historicalStats.minDeltaTime),
      historicalMaxTransactionDelta: historicalStats.maxTransactionDelta,
      busiestTime: formatTime(historicalStats.busiestTime),
      busiestTimeAvgQueue: Math.round(historicalStats.busiestAvgQueue),
      quietestTime: formatTime(historicalStats.quietestTime),
      quietestTimeAvgQueue: Math.round(historicalStats.quietestAvgQueue),
      peakTransactionTime: formatTime(historicalStats.peakTransactionTime),
      peakTransactionAvg: Math.round(historicalStats.peakTransactionAvg),
      dayAverageQueue: dayAverages.queue,
      dayAverageDelta: dayAverages.delta,
      dayAverageTransaction: dayAverages.transaction,
      totalSections: historicalData.length,
      activeSections: currentQueueTimes.filter(time => time > 0).length,
      sectionStats
    };
  }, []);

// Calculate count-specific stats
const calculateCountStats = useCallback((historicalPeopleCountData, sectionPeopleCounts) => {
    if (!historicalPeopleCountData?.length) return null;
  
    const currentCounts = Object.values(sectionPeopleCounts).filter(count => typeof count === 'number' && count >= 0);
    
    const currentStats = {
      maxCount: currentCounts.length > 0 ? Math.max(...currentCounts) : 0,
      minCount: currentCounts.length > 0 ? Math.min(...currentCounts) : 0,
      avgCount: currentCounts.length > 0 ? Math.round(currentCounts.reduce((sum, count) => sum + count, 0) / currentCounts.length) : 0,
    };
  
    const minDataPoints = Math.min(...historicalPeopleCountData.map(section => section.data?.length || 0));
    
    if (minDataPoints === 0) {
      return {
        ...currentStats,
        totalSections: historicalPeopleCountData.length,
        activeSections: currentCounts.filter(count => count > 0).length,
        sectionStats: {}
      };
    }
  
    let historicalStats = {
      busiestTime: null,
      busiestAvgCount: 0,
      quietestTime: null,
      quietestAvgCount: Infinity,
    };
  
    let dayTotals = {
      countSum: 0,
      validPoints: 0
    };
    
    for (let i = 0; i < minDataPoints; i++) {
      const timePoint = historicalPeopleCountData[0]?.data[i]?.x;
      if (!timePoint) continue;
  
      const pointCounts = historicalPeopleCountData
        .map(section => section.data[i]?.y)
        .filter(count => typeof count === 'number' && count >= 0);
  
      if (pointCounts.length === 0) continue;
  
      const pointStats = {
        maxCount: Math.max(...pointCounts),
        minCount: Math.min(...pointCounts),
        avgCount: pointCounts.reduce((sum, count) => sum + count, 0) / pointCounts.length,
      };
  
      dayTotals.countSum += pointStats.avgCount;
      dayTotals.validPoints++;
  
      const timeObj = new Date(timePoint);
      const timeString = timeObj.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
  
      if (pointStats.avgCount > historicalStats.busiestAvgCount) {
        historicalStats.busiestAvgCount = pointStats.avgCount;
        historicalStats.busiestTime = timeString;
      }
      if (pointStats.avgCount < historicalStats.quietestAvgCount) {
        historicalStats.quietestAvgCount = pointStats.avgCount;
        historicalStats.quietestTime = timeString;
      }
    }
  
    if (historicalStats.quietestAvgCount === Infinity) {
      historicalStats.quietestAvgCount = 0;
      historicalStats.quietestTime = null;
    }
  
    const dayAverages = {
      count: dayTotals.validPoints > 0 ? Math.round(dayTotals.countSum / dayTotals.validPoints) : 0,
    };
  
    const sectionStats = {};
    
    historicalPeopleCountData.forEach((sectionData) => {
      const sectionId = sectionData.section;
      
      if (!sectionData.data?.length) return;
  
      let sectionTotals = {
        maxCount: 0,
        minCount: Infinity,
        totalCount: 0,
        maxCountTime: null,
        minCountTime: null,
        dataPoints: 0
      };
  
      sectionData.data.forEach((point) => {
        const peopleCount = point.y;
        if (typeof peopleCount !== 'number' || peopleCount < 0) return;
  
        sectionTotals.totalCount += peopleCount;
        sectionTotals.dataPoints++;
  
        if (peopleCount > sectionTotals.maxCount) {
          sectionTotals.maxCount = peopleCount;
          sectionTotals.maxCountTime = new Date(point.x);
        }
        if (peopleCount < sectionTotals.minCount) {
          sectionTotals.minCount = peopleCount;
          sectionTotals.minCountTime = new Date(point.x);
        }
      });
  
      if (sectionTotals.dataPoints > 0) {
        sectionStats[sectionId] = {
          maxCount: sectionTotals.maxCount,
          minCount: sectionTotals.minCount === Infinity ? 0 : sectionTotals.minCount,
          avgCount: Math.round(sectionTotals.totalCount / sectionTotals.dataPoints),
          maxCountTime: sectionTotals.maxCountTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          }),
          minCountTime: sectionTotals.minCountTime?.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          })
        };
      }
    });
  
    return {
      currentMaxCount: currentStats.maxCount,
      currentMinCount: currentStats.minCount,
      currentAvgCount: currentStats.avgCount,
      busiestTime: historicalStats.busiestTime,
      busiestTimeAvgCount: Math.round(historicalStats.busiestAvgCount),
      quietestTime: historicalStats.quietestTime,
      quietestTimeAvgCount: Math.round(historicalStats.quietestAvgCount),
      dayAverageCount: dayAverages.count,
      totalSections: historicalPeopleCountData.length,
      activeSections: currentCounts.filter(count => count > 0).length,
      sectionStats
    };
  }, []);

  // Process section data for display
  const getSectionData = useCallback(() => {
    return sections
      .map((section, index) => ({
        id: index + 1,
        name: section,
        queueTime: sectionQueueTimes[section] || 0,
      }))
      .sort((a, b) => {
        switch (currentSort.value) {
          case 'id-asc':
            return a.id - b.id;
          case 'id-desc':
            return b.id - a.id;
          case 'current-queue-asc':
            return a.queueTime - b.queueTime;
          case 'current-queue-desc':
            return b.queueTime - a.queueTime;
          default:
            return a.id - b.id;
        }
      });
  }, [sections, sectionQueueTimes, currentSort.value]);

  // Get sorted sections for transaction times
  const getSortedTransactionSections = useCallback(() => {
    return [...sections].sort((a, b) => {
      const [sortBy, direction] = currentTransactionSort.value.split('-');
      const isAscending = direction === 'asc';
      
      let aValue, bValue;
      
      switch (sortBy) {
        case 'sectionId':
          aValue = parseInt(a);
          bValue = parseInt(b);
          break;
        case 'currentTransaction':
          aValue = sectionTransactionTimes[a] || 0;
          bValue = sectionTransactionTimes[b] || 0;
          break;
        case 'avgTransaction':
          aValue = stats?.sectionStats?.[a]?.avgTransaction || 0;
          bValue = stats?.sectionStats?.[b]?.avgTransaction || 0;
          break;
        case 'peakTransaction':
          aValue = stats?.sectionStats?.[a]?.maxTransaction || 0;
          bValue = stats?.sectionStats?.[b]?.maxTransaction || 0;
          break;
        default:
          return 0;
      }
      
      return isAscending ? aValue - bValue : bValue - aValue;
    });
  }, [sections, sectionTransactionTimes, stats, currentTransactionSort.value]);

  const getSortedSectionAnalysisSections = useCallback(() => {
    const isCountMode = currentLocation?.sensorDataType === "count";
    
    return [...sections].sort((a, b) => {
      const [sortBy, direction] = currentSectionAnalysisSort.value.split('-');
      const isAscending = direction === 'asc';
      
      let aValue, bValue;
      
      switch (sortBy) {
        case 'sectionId':
          aValue = parseInt(a);
          bValue = parseInt(b);
          break;
        case 'maxCount':
          aValue = countStats?.sectionStats?.[a]?.maxCount || 0;
          bValue = countStats?.sectionStats?.[b]?.maxCount || 0;
          break;
        case 'avgCount':
          aValue = countStats?.sectionStats?.[a]?.avgCount || 0;
          bValue = countStats?.sectionStats?.[b]?.avgCount || 0;
          break;
        case 'maxQueue':
          aValue = stats?.sectionStats?.[a]?.maxQueue || 0;
          bValue = stats?.sectionStats?.[b]?.maxQueue || 0;
          break;
        case 'avgQueue':
          aValue = stats?.sectionStats?.[a]?.avgQueue || 0;
          bValue = stats?.sectionStats?.[b]?.avgQueue || 0;
          break;
        case 'maxTransaction':
          aValue = stats?.sectionStats?.[a]?.maxTransaction || 0;
          bValue = stats?.sectionStats?.[b]?.maxTransaction || 0;
          break;
        case 'avgTransaction':
          aValue = stats?.sectionStats?.[a]?.avgTransaction || 0;
          bValue = stats?.sectionStats?.[b]?.avgTransaction || 0;
          break;
        default:
          return 0;
      }
      
      return isAscending ? aValue - bValue : bValue - aValue;
    });
  }, [sections, stats, countStats, currentSectionAnalysisSort.value, currentLocation?.sensorDataType]);

  // Utility functions
  const getQueueTimeColor = (time) => {
    if (time <= 10) return 'green';
    if (time <= 20) return 'orange';
    return 'red';
  };

  const handleSortChange = (sortOption) => {
    setCurrentSort(sortOption);
    setIsDropdownOpen(false);
  };

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Transaction sort handlers
  const handleTransactionSortChange = (sortOption) => {
    setCurrentTransactionSort(sortOption);
    setIsTransactionDropdownOpen(false);
  };

  const toggleTransactionDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsTransactionDropdownOpen(!isTransactionDropdownOpen);
  };

  // Section Analysis sort handlers
  const handleSectionAnalysisSortChange = (sortOption) => {
    setCurrentSectionAnalysisSort(sortOption);
    setIsSectionAnalysisDropdownOpen(false);
  };

  const toggleSectionAnalysisDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSectionAnalysisDropdownOpen(!isSectionAnalysisDropdownOpen);
  };

  // Auto refresh functionality
  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (autoFetchEnabled && !loading) {
      intervalRef.current = setInterval(() => {
        // console.log('Auto-fetching concession data...');
        fetchConcessionData(true);
      }, 60000); // every minute, same as the other verticals
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoFetchEnabled, loading, fetchConcessionData]);

  // Calculate stats when section data changes
    useEffect(() => {
    const isCountMode = currentLocation?.sensorDataType === "count";
    
    if (isCountMode && historicalPeopleCountData.length > 0) {
        const calculatedCountStats = calculateCountStats(
        historicalPeopleCountData, 
        sectionPeopleCounts
        );
        setCountStats(calculatedCountStats);
    } else if (!isCountMode && historicalData.length > 0) {
        const calculatedStats = calculateStats(
        historicalData, 
        sectionQueueTimes, 
        historicalTransactionData, 
        sectionTransactionTimes
        );
        setStats(calculatedStats);
    }
    }, [
    sectionQueueTimes, 
    historicalData, 
    historicalTransactionData, 
    sectionTransactionTimes, 
    sectionPeopleCounts,
    historicalPeopleCountData,
    currentLocation?.sensorDataType,
    calculateStats,
    calculateCountStats
    ]);

  // Initial data fetch
  useEffect(() => {
    if (currentLocation && selectedDate) {
      fetchConcessionData();
    }
  }, [currentLocation, selectedDate, fetchConcessionData]);

    // sync selectedDate with currentGame
    useEffect(() => {
        if (currentGame) {
          // console.log('Current game changed, clearing previous data...');
          // Clear all previous data when game changes
          setHistoricalData([]);
          setHistoricalTransactionData([]);
          setSectionQueueTimes({});
          setSectionTransactionTimes({});
          setStats(null);
          setAverageQueueTime(0);
          setAverageTransactionTime(0);
          setActiveSections(0);
          setTotalSections(0);
          setHistoricalPeopleCountData([]);
          setSectionPeopleCounts({});
          setAveragePeopleCount(0);
          setCountStats(null);
          
          setSelectedDate(moment(currentGame.date).toDate());
        }
      }, [currentGame]);

  // Redirect if no location ID
  useEffect(() => {
    if (!id) {
      navigate("/locations");
    }
  }, [id, navigate]);

  return children({
    // State
    loading,
    dayType,
    historicalData,
    historicalTransactionData,
    stats,
    isDropdownOpen,
    currentSort,
    sectionQueueTimes,
    sectionTransactionTimes,
    averageQueueTime,
    averageTransactionTime,
    stats,
    countStats,
    historicalPeopleCountData,
    sectionPeopleCounts,
    averagePeopleCount,
    activeSections,
    totalSections,
    autoFetchEnabled,
    
    // Transaction sorting state
    isTransactionDropdownOpen,
    currentTransactionSort,
    
    // Section Analysis sorting state
    isSectionAnalysisDropdownOpen,
    currentSectionAnalysisSort,
    
    // Computed data
    sections,
    sectionData: getSectionData(),
    sortOptions,
    
    // Transaction sorting data
    transactionSortOptions,
    sortedTransactionSections: getSortedTransactionSections(),
    
    // Section Analysis sorting data
    sectionAnalysisSortOptions,
    sortedSectionAnalysisSections: getSortedSectionAnalysisSections(),
    
    // Current location data
    currentLocation,
    id,

    // Game selector props
    games,
    now,
    currentSeries,
    currentGame,
    currentSeason,
    isToday,
    selectedDate,

    calculateStats,
    calculateCountStats,
 
    // Actions
    setDayType,
    setIsDropdownOpen,
    setCurrentSort,
    setAutoFetchEnabled,
    handleSortChange,
    toggleDropdown,
    setCurrentSeason,
    setCurrentGame,
    setCurrentSeries,
    setSelectedDate,
    refreshRegularDay,
    
    // Transaction sort actions
    setIsTransactionDropdownOpen,
    setCurrentTransactionSort,
    handleTransactionSortChange,
    toggleTransactionDropdown,
    
    // Section Analysis sort actions
    setIsSectionAnalysisDropdownOpen,
    setCurrentSectionAnalysisSort,
    handleSectionAnalysisSortChange,
    toggleSectionAnalysisDropdown,
    
    // API functions
    fetchConcessionData,
    
    // Utility functions
    getQueueTimeColor,
    formatTransactionTime,
    
    // Context and navigation
    authCtx,
    navigate
  });
};

export default ConcessionLocationController;